import { useEffect } from "react";
import {
  hasStoredNativeTokens,
  isNativeCapacitorApp,
} from "@/integrations/clients/carAuction/nativeAuth";
import {
  peekPendingBankIdReturn,
  takePendingBankIdReturn,
} from "@/lib/native/pendingBankIdReturn";

const DEFAULT_AUTH = "/auth";
const DEFAULT_HOME = "/dashboard";
const DEFAULT_GUEST = "/auctions";
const NATIVE_SESSION_KEY = "bilenia_native_app_session";
const GUEST_SESSION_KEY = "bilenia_native_guest_session";

function getHashPath(): string {
  const raw = window.location.hash.replace(/^#/, "");
  return raw.split("?")[0] || "";
}

function getHashSearchParams(): URLSearchParams {
  const raw = window.location.hash.replace(/^#/, "");
  const q = raw.indexOf("?");
  if (q < 0) return new URLSearchParams();
  return new URLSearchParams(raw.slice(q + 1));
}

/**
 * BankID / contract deep links land on #/auth?nativeCode=… (or bankidError=…).
 * After same-device BankID Android often kills the process ("App restarted") so
 * sessionStorage is empty — we must NOT treat that as a cold guest launch and
 * bounce to /auctions (that wiped nativeCode and left users stuck on auth).
 */
function hasNativeAuthReturnParams(): boolean {
  const params = getHashSearchParams();
  return (
    params.has("nativeCode") ||
    params.has("bankid") ||
    params.has("bankidError") ||
    params.has("bankidRegister") ||
    params.has("bankidRegisterError") ||
    params.has("nativeReturn") ||
    params.has("contractSignatureStatus")
  );
}

/** Known app routes (prefix match). Unknown hashes → cold-start default. */
function isRoutableHash(path: string): boolean {
  if (!path || path === "/") return false;
  const prefixes = [
    "/auth",
    "/dashboard",
    "/verify-email",
    "/accept-terms",
    "/reset-password",
    "/hur-det-fungerar",
    "/salj-bilen",
    "/kop-bil",
    "/how-it-works",
    "/sell-car",
    "/buy-car",
    "/auctions",
    "/listings",
    "/orders",
    "/my-auctions",
    "/settings",
    "/support",
    "/favorites",
    "/notifications",
    "/admin",
    "/car-test",
    "/list-or-value-car",
    "/dealer-portal",
  ];
  return prefixes.some((p) => path === p || path.startsWith(`${p}/`));
}

/**
 * Ensures Capacitor hash router has a valid route after React mounts.
 * Logged-in cold start → dashboard; in-session navigation is never overridden.
 * BankID return query params on /auth must never be wiped.
 */
const NativeHashBootstrap = () => {
  useEffect(() => {
    if (!isNativeCapacitorApp()) return;

    const hashPath = getHashPath();
    const hadSession = sessionStorage.getItem(NATIVE_SESSION_KEY);

    // Deep-link return (BankID etc.): keep /auth?… intact even on cold start.
    // Also honor a stashed pending return (Android process kill wipes the hash
    // before getLaunchUrl is applied into the router).
    const pendingReturn = peekPendingBankIdReturn();
    if (hasNativeAuthReturnParams() || pendingReturn) {
      sessionStorage.setItem(GUEST_SESSION_KEY, "1");
      if (pendingReturn && !hasNativeAuthReturnParams()) {
        const taken = takePendingBankIdReturn() || pendingReturn;
        const nextHash = `#/auth${taken.startsWith("?") ? taken : `?${taken}`}`;
        console.info("[NativeHashBootstrap] applying pending BankID return", nextHash);
        window.location.replace(
          `${window.location.pathname}${window.location.search}${nextHash}`,
        );
        return;
      }
      console.info("[NativeHashBootstrap] preserving auth return params", {
        hashPath,
        search: window.location.hash,
      });
      return;
    }

    if (hasStoredNativeTokens()) {
      sessionStorage.setItem(NATIVE_SESSION_KEY, "1");
      if (
        !hadSession ||
        hashPath === DEFAULT_AUTH ||
        !hashPath ||
        hashPath === "/"
      ) {
        if (hashPath !== DEFAULT_HOME) {
          window.location.replace(`/#${DEFAULT_HOME}`);
        }
      }
      return;
    }

    sessionStorage.removeItem(NATIVE_SESSION_KEY);
    const guestSession = sessionStorage.getItem(GUEST_SESSION_KEY);
    if (
      !guestSession &&
      (hashPath === DEFAULT_AUTH || !hashPath || hashPath === "/")
    ) {
      sessionStorage.setItem(GUEST_SESSION_KEY, "1");
      if (hashPath !== DEFAULT_GUEST) {
        window.location.replace(`/#${DEFAULT_GUEST}`);
      }
      return;
    }

    sessionStorage.setItem(GUEST_SESSION_KEY, "1");
    if (!isRoutableHash(hashPath)) {
      window.location.replace(`/#${DEFAULT_GUEST}`);
    }
  }, []);

  return null;
};

export default NativeHashBootstrap;
